const { h } = window.preact;
const { useState, useRef, useEffect } = window.preactHooks;
const html = window.htm.bind(h);

import { useRollenfinder } from "./rollenfinder-context.js";
import { Spinner } from "./Spinner.js";
import { getTranslation } from "./translations.js";

async function getDruckerHersteller() {
  try {
    const response = await fetch(`/apps/rollenfinder/manufacturers`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    const manufacturers = data.manufacturers;
    return manufacturers;
  } catch (error) {
    throw error;
  }
}

export function RollenfinderStep1({ error }) {
  const { state, updateState } = useRollenfinder();
  const [druckerHersteller, setDruckerHersteller] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredManufacturers, setFilteredManufacturers] =
    useState(druckerHersteller);
  const [activeLetter, setActiveLetter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const manufacturersListRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        setIsLoading(true);
        const manufacturers = await getDruckerHersteller();
        const manufacturerObjects = manufacturers.map((name) => ({
          name,
          id: name,
        }));
        setDruckerHersteller(manufacturerObjects);
        setFilteredManufacturers(manufacturerObjects);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };

    fetchManufacturers();
  }, []);

  // Add effect to update filtered manufacturers when druckerHersteller changes
  useEffect(() => {
    if (!searchQuery) {
      setFilteredManufacturers(druckerHersteller);
    } else {
      const results = druckerHersteller.filter((manufacturer) =>
        manufacturer.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredManufacturers(results);
    }
  }, [druckerHersteller, searchQuery]);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Group manufacturers by first letter
  const groupedManufacturers = filteredManufacturers.reduce(
    (acc, manufacturer) => {
      const firstLetter = manufacturer.name.charAt(0).toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(manufacturer);
      return acc;
    },
    {},
  );

  // Create alphabet array for navigation
  const alphabet = Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ");

  const scrollToLetter = (letter) => {
    if (
      !manufacturersListRef.current ||
      !Object.keys(groupedManufacturers).includes(letter)
    ) {
      return;
    }

    setActiveLetter(letter);
    const letterElement = manufacturersListRef.current.querySelector(
      `[data-letter="${letter}"]`,
    );
    if (letterElement) {
      letterElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleManufacturer = (manufacturer) => {
    const newManufacturers = state.selectedManufacturers.some(
      (m) => m.id === manufacturer.id,
    )
      ? state.selectedManufacturers.filter((m) => m.id !== manufacturer.id)
      : [...state.selectedManufacturers, manufacturer];

    updateState({ selectedManufacturers: newManufacturers });
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeManufacturer = (manufacturerId) => {
    updateState({
      selectedManufacturers: state.selectedManufacturers.filter(
        (m) => m.id !== manufacturerId,
      ),
    });
  };

  const clearAllManufacturers = () => {
    updateState({ selectedManufacturers: [] });
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return html`
    <div>
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <span class="text-base">${getTranslation("steps.step_1.title")}</span>
        </div>

        <!-- Search input with tags -->
        <div class="relative flex flex-col gap-2">
          ${error &&
          !searchQuery &&
          html` <span class="text-error text-base">${error}</span> `}
          <div
            class=${`flex min-h-[42px] w-full flex-wrap items-center gap-2 rounded-md border p-2 transition-colors relative
              ${error && !searchQuery ? "border-error" : "border-gray-300"}`}
          >
            ${state.selectedManufacturers.map(
              (manufacturer) => html`
                <div
                  key=${manufacturer.id}
                  onClick=${() => removeManufacturer(manufacturer.id)}
                  class="flex cursor-pointer items-center gap-1 rounded-md bg-[#6B8E23] px-2 py-1 text-sm text-white transition-colors hover:bg-[#87ab47]"
                >
                  <span>${manufacturer.name}</span>
                  <span class="ml-1">×</span>
                </div>
              `,
            )}
            <input
              ref=${searchInputRef}
              type="text"
              class="min-w-[120px] flex-1 border-none p-0 outline-none"
              placeholder=${getTranslation("steps.step_1.search_placeholder")}
              value=${searchQuery}
              onInput=${(e) => handleSearch(e.target.value)}
            />
            <div
              class="absolute top-1/2 right-2 flex -translate-y-1/2 transform items-center gap-2"
            >
              ${state.selectedManufacturers.length > 0 &&
              html`
                <button
                  onClick=${clearAllManufacturers}
                  class="hover:text-error cursor-pointer text-sm font-medium text-gray-500 transition-colors"
                >
                  ${getTranslation("steps.step_1.clear_all")}
                </button>
              `}
              ${searchQuery &&
              html`
                <button
                  class="cursor-pointer"
                  onClick=${() => setSearchQuery("")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              `}
            </div>
          </div>
        </div>

        ${isLoading
          ? html`<${Spinner} size="lg" />`
          : html`
              <div
                class="flex flex-col justify-between md:h-fit md:max-h-[320px] md:w-full md:flex-row"
              >
                <!-- Alphabet navigation -->
                <div
                  class="border-borders/50 mb-4 flex flex-row justify-start gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:mb-0 md:w-[426px] md:flex-wrap md:border-r md:pr-4 [&::-webkit-scrollbar]:hidden"
                >
                  ${alphabet.map(
                    (letter) => html`
                      <button
                        class=${`min-w-[40px] min-h-[40px] w-[40px] h-[40px] md:w-[54px] md:h-[54px] flex items-center justify-center rounded-md cursor-pointer transition-all duration-200 text-sm md:text-base shrink-0
                    ${
                      Object.keys(groupedManufacturers).includes(letter)
                        ? `${
                            activeLetter === letter
                              ? "bg-[#87ab47] text-white scale-105 shadow-md"
                              : "bg-[#6B8E23] text-white hover:bg-[#87ab47]"
                          }`
                        : "opacity-50 cursor-not-allowed"
                    }`}
                        key=${letter}
                        onClick=${() => {
                          if (
                            Object.keys(groupedManufacturers).includes(letter)
                          ) {
                            scrollToLetter(letter);
                          }
                        }}
                      >
                        ${letter}
                      </button>
                    `,
                  )}
                </div>
                <!-- Manufacturers list -->
                <div
                  ref=${manufacturersListRef}
                  class="grid grid-cols-2 gap-4 overflow-y-auto md:grid-cols-4"
                >
                  ${Object.entries(groupedManufacturers)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(
                      ([letter, manufacturers]) => html`
                        <div
                          key=${letter}
                          data-letter=${letter}
                          class=${`flex flex-col gap-2 p-2 rounded-md transition-all duration-200
                      ${activeLetter === letter ? "bg-[#f0f7e6]" : ""}`}
                        >
                          <h3
                            class="text-primary text-sm font-extrabold md:text-base"
                          >
                            ${letter}
                          </h3>
                          ${manufacturers.map(
                            (manufacturer) => html`
                              <button
                                key=${manufacturer.id}
                                class=${`text-left px-2 hover:text-primary transition-colors duration-200 cursor-pointer text-sm md:text-base
                              ${
                                state.selectedManufacturers.some(
                                  (m) => m.id === manufacturer.id,
                                )
                                  ? "bg-primary text-white rounded-md hover:text-white"
                                  : ""
                              }`}
                                onClick=${() =>
                                  toggleManufacturer(manufacturer)}
                              >
                                ${manufacturer.name}
                              </button>
                            `,
                          )}
                        </div>
                      `,
                    )}
                  ${filteredManufacturers.length === 0 &&
                  html`
                    <div
                      class="py-4 text-center text-sm text-gray-500 md:text-base"
                    >
                      ${getTranslation("steps.step_1.no_results")}
                    </div>
                  `}
                </div>
              </div>
            `}
      </div>
    </div>
  `;
}
